import { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-white pt-0 pb-8 md:pb-12">
      {/* Tour selector */}
      {hasMultiple && (
        <div className="flex justify-center md:justify-end max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <select
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(Number(e.target.value))}
            className="vt-selector appearance-none cursor-pointer font-heading text-sm uppercase tracking-[0.15em] rounded-full pl-9 pr-11 py-3 lg:pl-12 lg:pr-14 lg:py-4 lg:text-base transition-all focus:outline-none max-w-full truncate"
          >
            {validTours.map((tour, i) => (
              <option key={i} value={i}>
                {tour.virtual_tour_title || `Tour ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tour embed */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTour?.virtual_tour_url ? (
          <div className="relative w-full overflow-hidden border border-[var(--vibe-color-border)] pb-[112.5%] lg:pb-[56.25%]">
            <iframe
              key={activeTour.virtual_tour_url}
              src={activeTour.virtual_tour_url}
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
              title={activeTour.virtual_tour_title || 'Virtual Tour'}
            />
          </div>
        ) : (
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-muted)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        )}
      </div>

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .vt-selector {
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 1.5rem center;
          background-size: 0.85rem;
        }
        .vt-selector:hover {
          background-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        /* Native option list renders on the OS surface — keep it legible. */
        .vt-selector option {
          background-color: #fff;
          color: var(--vibe-color-text);
          text-transform: none;
          letter-spacing: normal;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as any).contentSchema = contentSchema;

export default VirtualTourEmbed;
